import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";

interface CheckboxAppearanceSelectorProps {
  initialInlineText?: string;
  onAppearanceChange: (inlineText?: string) => void;
}

const CheckboxAppearanceSelector: React.FC<CheckboxAppearanceSelectorProps> = ({
  initialInlineText,
  onAppearanceChange
}) => {
  const { t } = useTranslation();

  // Initialize state based on props - treat empty string as undefined
  const [selectedMode, setSelectedMode] = useState<
    "label_only" | "label_with_text"
  >(
    initialInlineText && initialInlineText.trim() !== ""
      ? "label_with_text"
      : "label_only"
  );
  const [inlineText, setInlineText] = useState(initialInlineText || "");

  // Handle radio button change
  const handleModeChange = (value: string) => {
    const mode = value as "label_only" | "label_with_text";
    setSelectedMode(mode);

    if (mode === "label_only") {
      // Clear inline text and notify parent
      setInlineText("");
      onAppearanceChange(undefined);
    } else {
      // Keep current text or initialize to empty, notify parent
      onAppearanceChange(inlineText);
    }
  };

  // Handle text input change
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setInlineText(text);
    onAppearanceChange(text);
  };

  // Update state when initialInlineText prop changes
  useEffect(() => {
    if (initialInlineText && initialInlineText.trim() !== "") {
      setSelectedMode("label_with_text");
      setInlineText(initialInlineText);
    } else {
      setSelectedMode("label_only");
      setInlineText("");
    }
  }, [initialInlineText]);

  return (
    <div className="space-y-4">
      <div>
        <Text variant="bodySecondary" className="block mb-3">
          {t("settings.deals.fieldConfiguration.checkboxAppearance")}
        </Text>
        <RadioGroup
          value={selectedMode}
          onValueChange={handleModeChange}
          className="space-y-3"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="label_only" id="label_only" />
            <Text
              as="label"
              htmlFor="label_only"
              variant="body"
              className="cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("settings.deals.fieldConfiguration.labelOnly")}
            </Text>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="label_with_text" id="label_with_text" />
            <Text
              as="label"
              htmlFor="label_with_text"
              variant="body"
              className="cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("settings.deals.fieldConfiguration.labelWithText")}
            </Text>
          </div>
        </RadioGroup>
      </div>

      {/* Conditional Text Input */}
      {selectedMode === "label_with_text" && (
        <div>
          <Text variant="bodySecondary" className="block mb-2">
            {t("settings.deals.fieldConfiguration.textNextToCheckbox")}
          </Text>
          <Input
            type="text"
            value={inlineText}
            onChange={handleTextChange}
            placeholder={t(
              "settings.deals.fieldConfiguration.addTextNextToCheckbox"
            )}
            required
          />
        </div>
      )}
    </div>
  );
};

export default CheckboxAppearanceSelector;
